import { ConfirmationDialog } from '@paintscout/ui'
import type { useDialogs } from '@paintscout/ui-business'

type Dialogs = Pick<ReturnType<typeof useDialogs>, 'openDialog' | 'dismissDialog'>

/**
 * Turning packages off drops the tier copy, prices and feature rows the estimator typed onto this
 * estimate — the drawer starts from the template again next time. Nothing recovers it, so say so.
 */
export const DISABLE_PACKAGES_MESSAGE =
  "This will remove all tier details you've entered. You'll need to re-enter them if you enable packages again."

/**
 * The classic editor's Remove (ENG-2705). That editor has no packages drawer, so once packages come
 * off an estimate there they cannot be added back — unlike the new editor's Disable, which can.
 *
 * Takes the account's own word for the document (`getObjectLabels(...).quote.value`), since a
 * client can rename "Estimate". Names the tiers by position: removal always keeps the base tier,
 * which is the middle one (`BASE_TIER_ID`).
 */
export function getRemovePackagesMessage(quoteLabel: string) {
  const label = quoteLabel.toLowerCase()
  return (
    `This ${label} will keep the Standard package. Basic and Premium will be removed. ` +
    `You won't be able to add tiered packages back to this ${label} until you re-enable the beta feature.`
  )
}

/**
 * Confirm switching tiered packages off on an estimate that already has them. Shared by the new
 * editor's Disable and the classic editor's Remove, which differ only in what they say.
 */
export function confirmDisablePackages({
  openDialog,
  dismissDialog,
  onConfirm,
  message = DISABLE_PACKAGES_MESSAGE
}: Dialogs & { onConfirm: () => void; message?: string }) {
  openDialog(ConfirmationDialog, {
    message,
    onConfirm: () => {
      dismissDialog()
      onConfirm()
    },
    onCancel: dismissDialog
  })
}
